import { useSubmit } from "@certn/api";
import { Text } from "@certn/cascadia";
import { showToast } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFormikContext } from "formik";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

interface ManualIdvFileUploadProps {
  caseId?: string;
  fieldPrefix: string;
  isDisabled: boolean;
}

const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export function ManualIdvFileUpload({
  caseId,
  fieldPrefix,
  isDisabled,
}: ManualIdvFileUploadProps) {
  const { t } = useTranslation();
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const { submit: getPresignedUrl } = useSubmit(
    "api/internal/client-portal/generate-presigned-url/"
  );

  const fileKeyMeta = getFieldMeta(`${fieldPrefix}.file_key`);
  const fileNameMeta = getFieldMeta(`${fieldPrefix}.file_name`);
  const hasFile = Boolean(fileKeyMeta.value);
  const uploadedFileName = String(fileNameMeta.value || "");

  const handleFile = async (file: File) => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      showToast(
        t("Invalid file type. Please upload a PDF, PNG, JPG or JPEG."),
        {
          type: "error",
        }
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      showToast(t("File size exceeds 10 MB limit."), { type: "error" });
      return;
    }

    setUploading(true);
    const key = `manual-idv/${caseId}/${uuidv4()}/${file.name}`;
    try {
      const response = (await getPresignedUrl({
        key,
        content_type: file.type,
      })) as { url: string; tags: string };

      const headers = new Headers();
      headers.append("Content-Type", file.type);
      if (response.tags) {
        headers.append("x-amz-tagging", response.tags);
      }
      await fetch(response.url, { method: "PUT", body: file, headers });
    } catch {
      console.warn("S3 upload unavailable, using local key for development");
    }

    try {
      setFieldValue(`${fieldPrefix}.file_key`, key);
      setFieldValue(`${fieldPrefix}.file_name`, file.name);
      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl("");
      }
      showToast(t("File uploaded successfully."), { type: "success" });
    } catch (e) {
      console.error(e);
      showToast(t("Failed to upload file. Please try again."), {
        type: "error",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <Text style="input/label">{t("Upload a photo of the document")}</Text>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(",")}
        onChange={handleFileSelect}
        className={styles.hiddenInput}
        disabled={isDisabled || uploading}
      />
      {hasFile ? (
        <div className={styles.filePreview}>
          {previewUrl && (
            <img src={previewUrl} alt="" className={styles.thumbnail} />
          )}
          <Text style="global/body-secondary" color="text-base">
            {uploadedFileName}
          </Text>
          <button
            type="button"
            className={styles.removeLink}
            onClick={() => {
              setFieldValue(`${fieldPrefix}.file_key`, "");
              setFieldValue(`${fieldPrefix}.file_name`, "");
              setPreviewUrl("");
            }}
            disabled={isDisabled}
          >
            {t("Remove")}
          </button>
        </div>
      ) : (
        <div
          className={css(styles.dropZone, isDragOver && styles.dropZoneActive)}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() =>
            !isDisabled && !uploading && fileInputRef.current?.click()
          }
        >
          {uploading ? (
            <Text style="global/body-secondary" color="text-weak">
              {t("Uploading...")}
            </Text>
          ) : (
            <>
              <div className={styles.uploadIcon}>&#8682;</div>
              <Text style="global/body-secondary">
                <span className={styles.browseLink}>{t("Browse files")}</span>{" "}
                {t("or drag and drop to upload")}
              </Text>
              <Text style="global/body-secondary" color="text-weak">
                {t("PDF, PNG, JPG or JPEG (max 10 MB)")}
              </Text>
            </>
          )}
        </div>
      )}
      {fileKeyMeta.touched && fileKeyMeta.error && (
        <Text style="global/body-secondary" color="text-danger">
          {fileKeyMeta.error}
        </Text>
      )}
    </div>
  );
}

const styles = {
  hiddenInput: css({
    display: "none",
  }),
  dropZone: css({
    border: "2px dashed var(--color-border-default, #ccc)",
    borderRadius: "var(--space-2)",
    padding: "var(--space-6) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-1)",
    cursor: "pointer",
    marginTop: "var(--space-2)",
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": {
      borderColor: "var(--color-border-strong, #999)",
    },
  }),
  dropZoneActive: css({
    borderColor: "var(--color-action-primary-default, #0066cc)",
    backgroundColor: "var(--surface-sunken, #f5f5f5)",
  }),
  filePreview: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    padding: "var(--space-3)",
    border: "1px solid var(--color-border-default, #e0e0e0)",
    borderRadius: "var(--border-radius-1, 4px)",
    marginTop: "var(--space-2)",
  }),
  thumbnail: css({
    width: "32px",
    height: "32px",
    objectFit: "cover",
    borderRadius: "var(--border-radius-1, 4px)",
    flexShrink: 0,
  }),
  removeLink: css({
    marginLeft: "auto",
    background: "none",
    border: "none",
    color: "var(--color-text-weak, #666)",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    padding: 0,
    textDecoration: "underline",
    "&:hover": {
      color: "var(--color-text-base, #333)",
    },
  }),
  uploadIcon: css({
    fontSize: "24px",
    color: "var(--color-text-weak, #666)",
  }),
  browseLink: css({
    textDecoration: "underline",
    cursor: "pointer",
  }),
};
